import { createSelector } from "reselect"
import { reduce, chain, pick, mapKeys, get, isEmpty, replace, mergeWith, isFinite, map, merge } from "lodash"
import { getFavoriteFactors } from "modules/portfolio-root/selectors/portfolios"
import { getRebalancedRiskSummary, getRiskSummary as getRiskOnlySummary } from "./risk"
import { getPortfolioPositions, getRebalancedPortfolioPositions } from "./positions"
import { getBetaSummary, getRebalancedBetaSummary } from "./beta"
import { getExposureSummary, getContentSetSummary, getRebalancedExposureSummary, getRebalancedContentSetSummary } from "./exposure"
import { getSelectedCategoryFactors } from "./categories"
import { getDecompositionType, getIsModelFactorsUsed } from "."
import { getPredictedFactorSensitivitySummary, getRebalancedPredictedFactorSensitivitySummary } from "./factorSensitivity/predicted"
import { getHistoricalFactorSensitivitySummary, getRebalancedHistoricalFactorSensitivitySummary } from "./factorSensitivity/historical"
import { getForecastContributorsTotal, getRebalancedForecastContributorsTotal } from "./forecast"
import { getRiskFactorsSummary } from "./risk/factors"

const getRiskSummary = createSelector(
  [getRiskOnlySummary, getRiskFactorsSummary],
  (summary, factors) => merge({}, summary, factors),
)

export const getSummary = createSelector(
  [getRiskSummary, getExposureSummary, getContentSetSummary, getBetaSummary, getPredictedFactorSensitivitySummary, getHistoricalFactorSensitivitySummary, getForecastContributorsTotal, getPortfolioPositions],
  computeSummary,
)

export const getRebalancedSummary = createSelector(
  [getRebalancedRiskSummary, getRebalancedExposureSummary, getRebalancedContentSetSummary, getRebalancedBetaSummary, getRebalancedPredictedFactorSensitivitySummary, getRebalancedHistoricalFactorSensitivitySummary, getRebalancedForecastContributorsTotal, getRebalancedPortfolioPositions],
  computeSummary,
)

export const getSummaryRows = createSelector(
  [getSummary, getRebalancedSummary],
  (summary, rebalancedSummary) => {
    if (!rebalancedSummary) { return [{ ...summary, isAggregate: true }] }
    const delta = mergeWith({}, rebalancedSummary, summary, (rebalancedValue, originalValue) => (isFinite(rebalancedValue) && isFinite(originalValue)) ? rebalancedValue - originalValue : undefined)
    return map([
      summary,
      rebalancedSummary,
      delta,
    ], row => ({ ...row, isAggregate: true }))
  },
)

export const getOrderedFactors = createSelector(
  [getSummary, getSelectedCategoryFactors, getFavoriteFactors, getIsModelFactorsUsed, getDecompositionType],
  (summary, factors, favoriteFactors, isModelFactorsUsed, decompositionType) => {
    return chain(factors)
      .map((factor, factorId) => {
        return {
          ...factor,
          id: factorId,
          ...get(summary, [decompositionType, "factors", factorId], { value: 0 }),
          favorite: get(favoriteFactors, factorId, false) && isModelFactorsUsed,
        }
      })
      .orderBy([factor => factor.favorite, factor => Math.abs(factor.value)], ["desc", "desc"])
      .value()
  },
)

function computeSummary(risk, exposure, contentSets, beta, predictedFactorSensitivity, historicalFactorSensitivity, forecastTotal, positions) {
  if (isEmpty(positions)) { return null }
  return {
    ...reduce(contentSets, (sets, contentSet, id) => ({
      ...sets,
      [replace(id, "_exposure", "")]: {
        factors: chain(contentSet)
          .get("0.factors")
          .keyBy("id")
          .value(),
      },
    }), {}),
    forecast: {
      totalExpectedReturn: forecastTotal,
    },
    exposure: {
      factors: mapKeys(exposure.factors, "id"),
    },
    risk: {
      summary: {
        total: risk.total,
        ...pick(get(risk, "attribution.summary"), ["specific", "factors"]),
      },
      factors: mapKeys(get(risk, "attribution.factors"), "id"),
    },
    predictedFactorSensitivity: {
      factors: mapKeys(predictedFactorSensitivity.factors, "id"),
    },
    historicalFactorSensitivity: {
      factors: mapKeys(historicalFactorSensitivity.factors, "id"),
    },
    beta,
    ...reduce(positions, (total, { economicExposure, positionPercentEquity, activePercentEquity }) => ({
      economicExposure: total.economicExposure + (economicExposure ?? 0),
      positionPercentEquity: total.positionPercentEquity + (positionPercentEquity ?? 0),
      activePercentEquity: total.activePercentEquity + (activePercentEquity ?? 0),
    }), { economicExposure: 0, positionPercentEquity: 0, activePercentEquity: 0 }),
  }
}
